<template>
  <div class="vehicle-make-list-wrapper">
    <Select
      class="vehicle-make-list__select"
      :placeholder="placeholder"
      :value="selectedValue"
      type="autocomplete"
      :options="makeOptions"
      :is-disabled="isDisabled"
      :is-loading="isSearching"
      :empty-text="emptyText"
      :select-first-by-default="false"
      value-key="id"
      label-key="name"
      @search="onSearch"
      @input="onInput"
      @change="onChange"
    >
      <template #option="{ option }">
        <p v-if="option" class="vehicle-make-list-option__info-text">
          {{ option.name }}
        </p>
      </template>
    </Select>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { Select } from '@/shared/ui/form';

const SEARCH_DELAY = 1000;

export default {
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: [String, Number, Object],
      default: null,
    },
    placeholder: {
      type: String,
      default: 'Виберіть марку транспортного засобу',
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: 'Нічого немає',
    },
  },
  data() {
    return {
      makeOptions: [],
      isSearching: false,
      searchTimeoutId: null,
    };
  },
  created() {
    const preload = [...this.getPreloadVehicleMakeList];

    if (this.value) {
      const name = typeof this.value === 'object' ? this.value.name : this.value;
      const id = typeof this.value === 'object' ? this.value.id : null;

      const exists = preload.some(m => m.name === name || (id && m.id === id));

      if (!exists) {
        preload.push({ id: id ?? name, name });
      }
    }

    this.makeOptions = preload;
  },
  beforeDestroy() {
    this.clearSearchTimeout();
  },
  components: { Select },
  computed: {
    ...mapGetters('vehicleModule', ['getPreloadVehicleMakeList', 'getModelsByMakeName']),
    selectedValue() {
      if (!this.value) return null;
      if (typeof this.value === 'object') {
        return this.value.id;
      }
      const found = this.makeOptions.find(
        m => m.name === this.value || String(m.id) === String(this.value)
      );
      return found ? found.id : this.value;
    },
  },
  methods: {
    ...mapActions('vehicleModule', ['findVehicleMake']),
    clearSearchTimeout() {
      if (this.searchTimeoutId) {
        clearTimeout(this.searchTimeoutId);
        this.searchTimeoutId = null;
      }
    },
    filterPreloadList(query) {
      if (!query) return this.getPreloadVehicleMakeList;
      const lower = query.toLowerCase();
      return this.getPreloadVehicleMakeList.filter(m =>
        m.name.toLowerCase().includes(lower)
      );
    },
    findOptionById(id) {
      if (id == null) return undefined;
      const idStr = String(id);
      return this.makeOptions.find(m =>
        String(m.id) === idStr || m.name === id
      );
    },
    extractModels(makeName) {
      if (!makeName) return [];

      const models = this.getModelsByMakeName(makeName);

      if (!Array.isArray(models)) return [];

      return models.map(({ id, name }) => ({ id, name }));
    },
    ensureOptionInList(option) {
      if (!option || !option.id || !option.name) return;

      const exists = this.makeOptions.some(
        m => m.id === option.id || m.name === option.name
      );

      if (!exists) {
        this.makeOptions = [...this.makeOptions, option];
      }
    },
    async loadAndEmitModels(makeName) {
      if (!makeName) {
        this.$emit('models-loaded', []);
        return;
      }

      try {
        const mark = await this.findVehicleMake(makeName);
        if (mark) {
          this.$emit('models-loaded', this.extractModels(mark.name));
        } else {
          this.$emit('models-loaded', []);
        }
      } catch (_) {
        this.$emit('models-loaded', []);
      }
    },
    async handleValueChange(newValue) {
      if (!newValue) {
        this.$emit('models-loaded', []);
        return;
      }

      const option = typeof newValue === 'object'
        ? newValue
        : { id: newValue, name: newValue };

      this.ensureOptionInList(option);
      await this.loadAndEmitModels(option.name);
    },
    onSearch(query) {
      this.clearSearchTimeout();

      if (!query) {
        this.makeOptions = [...this.getPreloadVehicleMakeList];
        this.isSearching = false;
        return;
      }

      this.makeOptions = this.filterPreloadList(query);

      this.searchTimeoutId = setTimeout(async () => {
        this.isSearching = true;
        try {
          const mark = await this.findVehicleMake(query);

          if (!mark) return;

          this.ensureOptionInList(mark);
        } finally {
          this.isSearching = false;
          this.searchTimeoutId = null;
        }
      }, SEARCH_DELAY);
    },
    onInput(value) {
      const selectedOption = this.findOptionById(value);
      const result = selectedOption || (value ? { id: value, name: value } : null);
      this.$emit('input', result);
    },
    async onChange(value) {
      const selectedOption = this.findOptionById(value);
      const result = selectedOption || (value ? { id: value, name: value } : null);
      this.$emit('change', result);
      await this.loadAndEmitModels(result?.name);
    },
  },
  watch: {
    value: {
      immediate: true,
      async handler(newValue) {
        if (!newValue) {
          this.$emit('models-loaded', []);
          return;
        }
        const option = typeof newValue === 'object'
          ? newValue
          : { id: newValue, name: newValue };

        this.ensureOptionInList(option);
        await this.loadAndEmitModels(option.name);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.vehicle-make-list-wrapper {
  position: relative;
  width: 100%;
}

.vehicle-make-list__select {
  width: 100%;
}

.vehicle-make-list-option__info-text {
  margin: 0;

  &:not(:last-child) {
    margin-bottom: 4px;
  }

  strong {
    font-weight: 600;
  }
}
</style>
